import { ShieldCheck, Search, BadgeCheck, FileText, XCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { verifyCertificate } from "../api/api";

export default function CertificateVerification() {
    const [verificationNo, setVerificationNo] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Toast function (copied from DashboardOverview)
    const showToast = (message, type = 'success') => {
        const existingToast = document.querySelector('.custom-toast');
        if (existingToast) existingToast.remove();

        const toast = document.createElement('div');
        toast.className = `custom-toast toast-${type}`;

        const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
        const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';

        toast.innerHTML = `
            <div style="display: flex; align-items: center; gap: 12px;">
                <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
                    ${icon}
                </div>
                <div style="flex: 1;">${message}</div>
                <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
            </div>
        `;

        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: ${bgColor};
            color: white;
            padding: 14px 20px;
            border-radius: 12px;
            z-index: 9999;
            font-family: system-ui, -apple-system, sans-serif;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
            min-width: 300px;
            max-width: 400px;
            cursor: pointer;
            animation: slideInRight 0.3s ease;
            z-index: 10000;
        `;

        if (!document.querySelector('#toast-styles')) {
            const style = document.createElement('style');
            style.id = 'toast-styles';
            style.textContent = `
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes slideOutRight {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(100%); opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        }

        document.body.appendChild(toast);

        setTimeout(() => {
            if (toast && toast.parentElement) {
                toast.style.animation = 'slideOutRight 0.3s ease';
                setTimeout(() => {
                    if (toast && toast.parentElement) toast.remove();
                }, 300);
            }
        }, 3000);

        toast.onclick = () => {
            if (toast && toast.parentElement) {
                toast.style.animation = 'slideOutRight 0.3s ease';
                setTimeout(() => {
                    if (toast && toast.parentElement) toast.remove();
                }, 300);
            }
        };
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        
        if (!verificationNo.trim() || !name.trim()) {
            showToast("Please fill in all required fields", "error");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await verifyCertificate(verificationNo.trim(), name.trim());            
            if (response.success === true) {
                showToast("Certificate verified successfully!", "success");
                setVerificationNo("");
                setName("");
            } else {
                const errorMsg = response.message || "Certificate not found. Please check the details and try again.";
                setError(errorMsg);
                showToast(errorMsg, "error");
            }
        } catch (err) {
            const errorMsg = err.message || "Failed to verify certificate. Please try again.";
            setError(errorMsg);
            showToast(errorMsg, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setVerificationNo("");
        setName("");
        setError(null);
    };

    return (
        <div className="bg-[#f7faf9] font-lato">

            {/* Hero */}
            <section className="bg-gradient-to-b from-[#edf8f5] to-[#f8fbfb] py-16">
                <div className="max-w-5xl mx-auto px-5 text-center">

                    <h1 className="text-4xl font-bold text-gray-800">
                        Verify a <span className="text-[#17a673]">Nutrillion Certificate</span>
                    </h1>

                    <p className="text-gray-500 mt-3 mx-auto">
                        Enter the certificate details below to instantly verify the authenticity of
                        any certificate issued by Nutrillion Academy.
                    </p>

                    <div className="mt-12 mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

                        <div className="border-b px-7 py-5 flex items-start gap-3">
                            <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center">
                                <ShieldCheck className="text-green-600" size={22} />
                            </div>
                            <div className="text-left">
                                <h3 className="font-semibold text-gray-800">
                                    Certificate Verification Form
                                </h3>
                                <p className="text-sm text-gray-400">
                                    Fill in the required fields to verify authenticity
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleVerify} className="p-7 space-y-5 text-left">
                            <div>
                                <label className="text-md font-bold text-gray-700 block">
                                    Certificate ID <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-2 relative">
                                    <FileText
                                        size={18}
                                        className="absolute left-3 top-4 text-gray-400"
                                    />
                                    <input
                                        type="text"
                                        placeholder="e.g. VN-2026-NA-4892"
                                        value={verificationNo}
                                        onChange={(e) => setVerificationNo(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-green-500 outline-none"
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                    You can find the Certificate ID printed at the bottom of your certificate.
                                </p>
                            </div>
                            <div>
                                <label className="text-md font-bold text-gray-700 block">
                                    Full Name (as on certificate) <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-2 relative">
                                    <Search
                                        size={18}
                                        className="absolute left-3 top-4 text-gray-400"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Enter the full name printed on the certificate"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-green-500 outline-none"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                                    <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 py-3 rounded-lg bg-gradient-to-r from-[#18b66d] to-[#16a4d6] hover:opacity-95 text-white font-semibold transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={20} className="animate-spin" />
                                            Verifying...
                                        </>
                                    ) : (
                                        "Verify Certificate"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            <section className="py-6">
                <div className="max-w-6xl mx-auto px-5">
                    <h2 className="text-2xl font-bold text-center text-gray-800">
                        How Certificate Verification Works
                    </h2>

                    <div className="grid md:grid-cols-3 gap-7 mt-12">
                        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center">
                            <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center mx-auto">
                                <FileText className="text-green-600" />
                            </div>
                            <h3 className="font-bold mt-5">
                                Enter Certificate ID
                            </h3>
                            <p className="text-gray-500 text-sm mt-3 leading-6">
                                Locate the unique Certificate ID
                                printed on your Nutrillion certificate
                                and enter it in the form above.
                            </p>
                        </div>

                        {/* Card 2 */}
                        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center">
                            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto">
                                <Search className="text-blue-600" />
                            </div>
                            <h3 className="font-bold mt-5">
                                Instant Lookup
                            </h3>
                            <p className="text-gray-500 text-sm mt-3 leading-6">
                                Our system instantly matches the
                                provided details against our secure
                                certification database in real time.
                            </p>
                        </div>

                        {/* Card 3 */}
                        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center">
                            <div className="w-14 h-14 rounded-xl bg-yellow-100 flex items-center justify-center mx-auto">
                                <BadgeCheck className="text-yellow-600" />
                            </div>
                            <h3 className="font-bold mt-5">
                                Verification Result
                            </h3>
                            <p className="text-gray-500 text-sm mt-3 leading-6">
                                Receive an immediate verification
                                result confirming the certificate's
                                authenticity, issuer, and completion
                                date.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
}